import React from 'react';
import ReactDOM from 'react-dom/client';
import {createBrowserRouter, RouterProvider} from 'react-router-dom';
import * as Sentry from '@sentry/react';

const App = React.lazy(() => import('./pages/app'));
const Register = React.lazy(() => import('./pages/register'));
const ErrorPage = React.lazy(() => import('./pages/error'));

import './i18n';
import 'react-toastify/dist/ReactToastify.css';
import { RegisterLoaderData, RootLoaderData } from './types';


declare const window: {
  customEnv: {
    [key: string]: string
  },
  location: Location
};


if (window.customEnv && typeof window.customEnv.SENTRY_DSN !== 'undefined') {
  try {
    Sentry.init({
      dsn: window.customEnv.SENTRY_DSN,
      environment: 'production',
      tracesSampleRate: 0.2
    });

    console.log('Sentry initialized');
  } catch (e) {
    console.log(e);
    console.error('Sentry failed');
  }
} else {
  console.warn('SENTRY_DSN missing');
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    loader: ({request}): RootLoaderData => {
      const url = new URL(request.url);
      const hash = url.searchParams.get("h");

      if (!hash)
        throw new Error('Missing parameter "h"');

      return {hash};
    },
    errorElement: <ErrorPage />
  },
  {
    path: '/ru',
    element: <Register />,
    loader:({request}): RegisterLoaderData => {
      const url = new URL(request.url);
      const token = url.searchParams.get("t");

      if (!token)
        throw new Error('Missing parameter "token"');

      return {token, objtype: 'keyuser'};
    },
    errorElement: <ErrorPage />
  },
  {
    path: '/rs',
    element: <Register />,
    loader:({request}): RegisterLoaderData => {
      const url = new URL(request.url);
      const token = url.searchParams.get("t");

      if (!token)
        throw new Error('Missing parameter "token"');

      return {token, objtype: 'siteuser'};
    },
    errorElement: <ErrorPage />
  },
]);

interface ErrorFallbackProps {
  error: any;
  componentStack: string;
  eventId: string;
  resetError(): void;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({error, resetError}) => {
  return (
    <div className="p-6 text-center">
      <h1 className="text-2xl font-bold text-red-600">Something went wrong</h1>
      <p className="text-lg text-gray-700 mt-4">
        An unexpected error occurred. Our team has been notified.
      </p>
      <details className="mt-4 p-4 bg-gray-100 text-gray-600 rounded">
        <summary className="cursor-pointer">Error Details</summary>
        <pre className="mt-2">{error.message}</pre>
      </details>
      <button
        className="mt-6 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        onClick={resetError}
      >
        Try Again
      </button>
    </div>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <Sentry.ErrorBoundary fallback={(props) => <ErrorFallback {...props}/>} showDialog>
    <RouterProvider router={router} />
  </Sentry.ErrorBoundary>
);

